import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { guestService, processImageForUpload } from '../services/guestService';
import { notifyVisit } from '../services/notifyService';
import { EventResponse, PhotoResponse } from '../types';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #0f0f23;
  color: #e2e8f0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Header = styled.div`
  background: #1a1a35;
  border-bottom: 1px solid #2d2d5e;
  padding: 1.5rem 1.5rem 2rem;
  text-align: center;
`;

const EventName = styled.h1`
  font-size: clamp(1.5rem, 4vw, 2.2rem);
  font-weight: 900;
  margin: 0 0 0.5rem;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const EventDesc = styled.p`
  color: #94a3b8;
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
`;

const UploadArea = styled.div`
  max-width: 480px;
  margin: 0 auto;
`;

const UploadForm = styled.div`
  background: #242445;
  border: 1px dashed #7c3aed;
  border-radius: 16px;
  padding: 1.5rem;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  @media (max-width: 400px) { grid-template-columns: 1fr; }
`;

const Input = styled.input`
  background: #0f0f23;
  border: 1px solid #2d2d5e;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  color: #e2e8f0;
  font-size: 0.9rem;
  width: 100%;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #7c3aed; }
`;

const FileInput = styled.input`
  display: none;
`;

const FileLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  border: none;
  border-radius: 10px;
  padding: 0.85rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.75rem;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

const UploadBtn = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.85rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Progress = styled.div<{ pct: number }>`
  height: 4px;
  background: #2d2d5e;
  border-radius: 2px;
  margin-top: 0.5rem;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${p => p.pct}%;
    background: linear-gradient(90deg, #7c3aed, #ec4899);
    transition: width 0.3s;
  }
`;

const Message = styled.p<{ type?: 'error' | 'success' }>`
  font-size: 0.9rem;
  color: ${p => p.type === 'error' ? '#ef4444' : '#10b981'};
  margin-top: 0.5rem;
  text-align: center;
`;

const GallerySection = styled.div`
  max-width: 1100px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

const GalleryTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 1.25rem;
  color: #94a3b8;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
  @media (min-width: 600px) { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); }
`;

const PhotoCard = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #1a1a35;
  aspect-ratio: 1;
`;

const PhotoPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a1a35, #2d2d5e);
  border-radius: 12px;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%);
    animation: shimmer 1.4s infinite;
  }
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const PhotoImg = styled.img<{ loaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  cursor: zoom-in;
  opacity: ${p => p.loaded ? 1 : 0};
  transition: opacity 0.3s ease, transform 0.2s;
  will-change: transform;
  &:hover { transform: scale(1.03); }
`;

const PhotoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  padding: 1.5rem 0.75rem 0.75rem;
  opacity: 0;
  transition: opacity 0.2s;
  ${PhotoCard}:hover & { opacity: 1; }
`;

const PhotoAuthor = styled.p`
  color: #e2e8f0;
  font-size: 0.8rem;
  margin: 0 0 0.4rem;
`;

const DeleteBtn = styled.button`
  background: rgba(239,68,68,0.8);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  &:hover { background: #ef4444; }
`;

const DownloadBtn = styled.button`
  background: rgba(30,64,175,0.8);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  &:hover { background: #2563eb; }
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const LightboxImg = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255,255,255,0.1);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 10;
  &:hover { background: rgba(255,255,255,0.2); }
`;

const LightboxNav = styled.button<{ side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${p => p.side}: 1rem;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.15);
  color: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  font-size: 1.4rem;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255,255,255,0.3); }
`;

const LightboxDownload = styled.a`
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255,255,255,0.15);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  z-index: 10;
  &:hover { background: rgba(255,255,255,0.25); }
`;

const LightboxCounter = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255,255,255,0.6);
  font-size: 0.85rem;
  z-index: 10;
`;

const SlideshowOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: #000;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SlideshowImg = styled.img`
  max-width: 100%;
  max-height: 100vh;
  object-fit: contain;
  animation: slideFadeIn 0.6s ease forwards;
  @keyframes slideFadeIn {
    from { opacity: 0; transform: scale(1.04); }
    to   { opacity: 1; transform: scale(1); }
  }
`;

const SlideshowClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255,255,255,0.12);
  color: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  font-size: 1.3rem;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255,255,255,0.25); }
`;

const SlideshowCounter = styled.div`
  position: absolute;
  top: 1.1rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255,255,255,0.55);
  font-size: 0.85rem;
  z-index: 10;
`;

const SlideshowProgress = styled.div<{ duration: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #ec4899);
  animation: progress ${p => p.duration}ms linear forwards;
  @keyframes progress {
    from { width: 0%; }
    to   { width: 100%; }
  }
`;

const DisabledBanner = styled.div`
  background: rgba(239,68,68,0.1);
  border: 1px solid rgba(239,68,68,0.3);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  text-align: center;
  color: #fca5a5;
  max-width: 480px;
  margin: 0 auto;
`;

const BookmarkTooltip = styled.div<{ visible: boolean }>`
  position: fixed;
  bottom: 5rem;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a35;
  border: 1px solid #7c3aed;
  border-radius: 12px;
  padding: 0.85rem 1.25rem;
  font-size: 0.9rem;
  color: #e2e8f0;
  text-align: center;
  line-height: 1.6;
  z-index: 2000;
  pointer-events: none;
  opacity: ${p => p.visible ? 1 : 0};
  transition: opacity 0.3s;
  white-space: nowrap;
  box-shadow: 0 8px 32px rgba(124,58,237,0.3);
`;

const GuestEventPage: React.FC = () => {
  const { eventCode } = useParams<{ eventCode: string }>();
  const { t } = useTranslation();
  const [event, setEvent] = useState<EventResponse | null>(null);
  const [photos, setPhotos] = useState<PhotoResponse[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' } | null>(null);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const touchStartX = useRef<number>(0);
  const [myTokens, setMyTokens] = useState<Record<number, string>>(() => {
    try { return JSON.parse(localStorage.getItem('fotovibe_tokens') || '{}'); } catch { return {}; }
  });
  const fileRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [bookmarkTip, setBookmarkTip] = useState(false);
  const [isSlideshow, setIsSlideshow] = useState(false);
  const [slideshowLoading, setSlideshowLoading] = useState(false);
  const [slideshowList, setSlideshowList] = useState<PhotoResponse[]>([]);
  const [slideshowIdx, setSlideshowIdx] = useState(0);
  const slideshowIdxRef = useRef(0);
  const knownSlideshowIds = useRef<Set<number>>(new Set());
  const SLIDE_DURATION = 5000;

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const startSlideshow = () => {
    if (photos.length === 0) return;
    const list = [...photos];
    knownSlideshowIds.current = new Set(list.map(p => p.id));
    setSlideshowList(list);
    setSlideshowIdx(0);
    slideshowIdxRef.current = 0;
    setSlideshowLoading(true);

    // Precargar todos los thumbnails en background
    list.forEach(p => {
      if (p.thumbnailUrl) { const img = new Image(); img.src = p.thumbnailUrl; }
    });

    // Esperar a que cargue la primera foto antes de mostrar el slideshow
    if (list[0]?.thumbnailUrl) {
      const first = new Image();
      first.onload = () => { setSlideshowLoading(false); setIsSlideshow(true); };
      first.onerror = () => { setSlideshowLoading(false); setIsSlideshow(true); };
      first.src = list[0].thumbnailUrl;
    } else {
      setSlideshowLoading(false);
      setIsSlideshow(true);
    }
  };

  const stopSlideshow = () => setIsSlideshow(false);

  // Detectar fotos nuevas durante el slideshow e insertarlas justo después de la actual
  useEffect(() => {
    if (!isSlideshow || photos.length === 0) return;
    const newPhotos = photos.filter(p => !knownSlideshowIds.current.has(p.id));
    if (newPhotos.length === 0) return;
    newPhotos.forEach(p => knownSlideshowIds.current.add(p.id));
    setSlideshowList(prev => {
      const next = [...prev];
      next.splice(slideshowIdxRef.current + 1, 0, ...newPhotos);
      return next;
    });
  }, [photos, isSlideshow]);

  // Timer de avance automático
  useEffect(() => {
    if (!isSlideshow) return;
    const timer = setInterval(() => {
      setSlideshowIdx(prev => {
        const next = prev + 1;
        slideshowIdxRef.current = next;
        return next;
      });
    }, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [isSlideshow, SLIDE_DURATION]);

  // Cerrar al llegar al final
  useEffect(() => {
    if (isSlideshow && slideshowList.length > 0 && slideshowIdx >= slideshowList.length) {
      setIsSlideshow(false);
    }
  }, [slideshowIdx, slideshowList.length, isSlideshow]);

  const handleBookmark = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') setInstallPrompt(null);
    } else {
      const isMac = /Mac|iPhone|iPad/i.test(navigator.userAgent);
      setBookmarkTip(true);
      setTimeout(() => setBookmarkTip(false), 3500);
    }
  };

  const loadGallery = useCallback(async () => {
    if (!eventCode) return;
    try {
      const [gallery, ev] = await Promise.all([
        guestService.getGallery(eventCode),
        guestService.getPublicEvent(eventCode),
      ]);
      setPhotos(gallery);
      setEvent(ev);
    } catch (e) { console.error(e); }
  }, [eventCode]);

  useEffect(() => {
    if (!eventCode) return;
    guestService.getPublicEvent(eventCode)
      .then(ev => {
        setEvent(ev);
        notifyVisit('guest', `Evento: "${ev.name}" | Código: ${eventCode} | Fecha: ${ev.eventDate || 'sin fecha'}`);
      })
      .catch(() => setEvent(null));
    loadGallery();
    pollRef.current = setInterval(loadGallery, 10000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [eventCode, loadGallery]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
    setMessage(null);
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const handleUpload = async () => {
    if (!selectedFiles.length || !eventCode) return;
    setUploading(true);
    setMessage(null);
    const total = selectedFiles.length;
    setUploadProgress({ current: 0, total });
    let tokens = { ...myTokens };
    let uploaded = 0;
    let errors = 0;
    for (let i = 0; i < total; i++) {
      setUploadProgress({ current: i + 1, total });
      setProgress(Math.round((i / total) * 100));
      if (selectedFiles[i].size > MAX_FILE_SIZE) {
        errors++;
        continue;
      }
      try {
        const { original, thumbnail } = await processImageForUpload(selectedFiles[i]);
        const photo = await guestService.uploadPhoto(eventCode, original, thumbnail, name, email);
        tokens[photo.id] = photo.invitadoToken!;
        uploaded++;
      } catch (e: any) {
        errors++;
      }
    }
    setMyTokens(tokens);
    localStorage.setItem('fotovibe_tokens', JSON.stringify(tokens));
    setProgress(100);
    if (errors === 0) {
      setMessage({ text: uploaded === 1 ? t('guest.success_one', { n: uploaded }) : t('guest.success_many', { n: uploaded }), type: 'success' });
    } else {
      setMessage({ text: t('guest.partial', { ok: uploaded, s: uploaded !== 1 ? 's' : '', err: errors }), type: 'error' });
    }
    setSelectedFiles([]);
    if (fileRef.current) fileRef.current.value = '';
    await loadGallery();
    setUploading(false);
    setTimeout(() => setProgress(0), 1000);
  };

  const handleDelete = async (photoId: number) => {
    if (!eventCode) return;
    const token = myTokens[photoId];
    if (!token) return;
    if (!window.confirm(t('guest.confirm_delete'))) return;
    try {
      await guestService.deleteOwnPhoto(eventCode, photoId, token);
      const newTokens = { ...myTokens };
      delete newTokens[photoId];
      setMyTokens(newTokens);
      localStorage.setItem('fotovibe_tokens', JSON.stringify(newTokens));
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch { alert(t('guest.err_delete')); }
  };

  const preloadAdjacent = (idx: number) => {
    [-1, 1, 2].forEach(offset => {
      const p = photos[(idx + offset + photos.length) % photos.length];
      const url = p?.thumbnailUrl || p?.fileUrl;
      if (url) { const img = new Image(); img.src = url; }
    });
  };

  const prevPhoto = () => setLightboxIdx(i => {
    if (i === null) return null;
    const next = (i - 1 + photos.length) % photos.length;
    preloadAdjacent(next);
    return next;
  });
  const nextPhoto = () => setLightboxIdx(i => {
    if (i === null) return null;
    const next = (i + 1) % photos.length;
    preloadAdjacent(next);
    return next;
  });

  useEffect(() => {
    if (lightboxIdx === null && !isSlideshow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setLightboxIdx(null); setIsSlideshow(false); }
      else if (e.key === 'ArrowRight') nextPhoto();
      else if (e.key === 'ArrowLeft') prevPhoto();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, isSlideshow, photos.length]);

  const handleDownloadPhoto = (photo: { fileUrl: string; originalFilename: string }) => {
    fetch(photo.fileUrl)
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = photo.originalFilename || 'foto.jpg';
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  if (!event) return (
    <Wrapper>
      <div style={{ textAlign: 'center', padding: '6rem 2rem', color: '#94a3b8' }}>
        <p style={{ fontSize: '3rem' }}>🔍</p>
        <h2 style={{ color: '#e2e8f0' }}>{t('guest.not_found')}</h2>
        <p>{t('guest.not_found_sub')}</p>
      </div>
    </Wrapper>
  );

  const canUpload = event.uploadEnabled && event.status === 'ACTIVE';

  return (
    <Wrapper>
      <Header>
        <EventName>{event.name}</EventName>
        {event.description && <EventDesc>{event.description}</EventDesc>}

        <UploadArea>
          {!canUpload ? (
            <DisabledBanner>
              {event.status !== 'ACTIVE' ? t('guest.inactive') : t('guest.uploads_disabled')}
            </DisabledBanner>
          ) : (
            <UploadForm>
              <InputRow>
                <Input type="text" placeholder={t('guest.placeholder_name')} value={name} onChange={e => setName(e.target.value)} />
                <Input type="email" placeholder={t('guest.placeholder_email')} value={email} onChange={e => setEmail(e.target.value)} />
              </InputRow>
              <FileLabel htmlFor="photo-upload">
                {t('guest.btn_select')}
              </FileLabel>
              <FileInput
                id="photo-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                onChange={handleFileChange}
                ref={fileRef}
              />
              <UploadBtn onClick={handleUpload} disabled={!selectedFiles.length || uploading}>
                {uploading
                  ? t('guest.uploading', { current: uploadProgress.current, total: uploadProgress.total })
                  : selectedFiles.length !== 1 ? t('guest.btn_upload_plural') : t('guest.btn_upload')}
              </UploadBtn>
              {progress > 0 && <Progress pct={progress} />}
              {message && <Message type={message.type}>{message.text}</Message>}
            </UploadForm>
          )}
        </UploadArea>
      </Header>

      <GallerySection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          {/* Izquierda: reload + home + compartir */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button onClick={loadGallery} title={t('guest.title_reload')}
              style={{ background: '#2d2d5e', color: '#e2e8f0', border: 'none', borderRadius: '8px', padding: '0.6rem 0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
              </svg>
            </button>

            <a href="https://fotovibe.mx" target="_blank" rel="noopener noreferrer" title={t('guest.title_home')}
              style={{ background: '#2d2d5e', color: '#e2e8f0', border: 'none', borderRadius: '8px', padding: '0.6rem 0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </a>

            <button title={t('guest.title_slideshow')} onClick={startSlideshow} disabled={slideshowLoading}
              style={{ background: '#2d2d5e', color: '#e2e8f0', border: 'none', borderRadius: '8px', padding: '0.6rem 0.75rem', cursor: slideshowLoading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', opacity: slideshowLoading ? 0.6 : 1 }}>
              {slideshowLoading
                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite' }}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              }
            </button>

            <button title={t('guest.title_bookmark')} onClick={handleBookmark}
              style={{ background: '#2d2d5e', color: '#e2e8f0', border: 'none', borderRadius: '8px', padding: '0.6rem 0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
            </button>

            <button title={t('guest.title_share')}
              onClick={async () => {
                const eventName = event?.name || 'este evento';
                const shareText = t('guest.share_text');
                const shareUrl = window.location.href;

                if (navigator.share) {
                  try {
                    const resp = await fetch('https://fotovibe.mx/ficha-informativa.png');
                    const blob = await resp.blob();
                    const file = new File([blob], 'FotoVibe-ficha.png', { type: 'image/png' });
                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                      await navigator.share({ files: [file], title: t('guest.share_title', { name: eventName }), text: shareText });
                    } else {
                      await navigator.share({ title: t('guest.share_title', { name: eventName }), text: shareText, url: shareUrl });
                    }
                  } catch {
                    navigator.share({ title: t('guest.share_title', { name: eventName }), text: shareText, url: shareUrl }).catch(() => {});
                  }
                } else {
                  const enc = encodeURIComponent(shareUrl);
                  const txt = encodeURIComponent(shareText + '\n' + shareUrl);
                  const links = [
                    `WhatsApp → https://wa.me/?text=${txt}`,
                    `Facebook → https://www.facebook.com/sharer/sharer.php?u=${enc}`,
                    `Twitter/X → https://twitter.com/intent/tweet?text=${txt}`,
                    `Telegram → https://t.me/share/url?url=${enc}&text=${encodeURIComponent(shareText)}`,
                  ].join('\n');
                  alert(t('guest.share_choose') + links);
                }
              }}
              style={{ background: '#2d2d5e', color: '#e2e8f0', border: 'none', borderRadius: '8px', padding: '0.6rem 0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
          </div>

          {/* Derecha: solo conteo */}
          <GalleryTitle style={{ margin: 0 }}>{t('guest.gallery_title', { n: photos.length })}</GalleryTitle>
        </div>
        {photos.length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '3rem' }}>
            {t('guest.empty_gallery')}
          </p>
        ) : (
          <Grid>
            {photos.map((photo, idx) => (
              <PhotoCard key={photo.id}>
                <PhotoPlaceholder />
                {photo.thumbnailUrl && (
                  <PhotoImg
                    src={photo.thumbnailUrl}
                    alt={photo.originalFilename}
                    loading="lazy"
                    loaded={false}
                    onLoad={e => {
                      (e.target as HTMLImageElement).style.opacity = '1';
                      const prev = (e.target as HTMLImageElement).previousElementSibling as HTMLElement;
                      if (prev) prev.style.display = 'none';
                    }}
                    onClick={() => { setLightboxIdx(idx); preloadAdjacent(idx); }}
                  />
                )}
                <PhotoOverlay>
                  <PhotoAuthor>{photo.invitadoName || t('guest.anonymous')}</PhotoAuthor>
                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                    <DownloadBtn onClick={() => handleDownloadPhoto(photo)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </DownloadBtn>
                    {myTokens[photo.id] && (
                      <DeleteBtn onClick={() => handleDelete(photo.id)}>🗑</DeleteBtn>
                    )}
                  </div>
                </PhotoOverlay>
              </PhotoCard>
            ))}
          </Grid>
        )}
      </GallerySection>

      {isSlideshow && slideshowList[slideshowIdx] && (
        <SlideshowOverlay>
          <SlideshowClose onClick={stopSlideshow}>✕</SlideshowClose>
          <SlideshowCounter>{slideshowIdx + 1} / {slideshowList.length}</SlideshowCounter>
          <SlideshowImg
            key={slideshowList[slideshowIdx].id}
            src={slideshowList[slideshowIdx].thumbnailUrl}
            alt="Slideshow"
          />
          <SlideshowProgress key={`p-${slideshowList[slideshowIdx].id}`} duration={SLIDE_DURATION} />
        </SlideshowOverlay>
      )}

      <BookmarkTooltip visible={bookmarkTip}>
        Presiona <strong>{t('guest.bookmark_mac')}</strong> o <strong>{t('guest.bookmark_win')}</strong><br/>{t('guest.bookmark_tip')}
      </BookmarkTooltip>

      {lightboxIdx !== null && photos[lightboxIdx] && (
        <Lightbox
          onClick={() => setLightboxIdx(null)}
          onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            const delta = e.changedTouches[0].clientX - touchStartX.current;
            if (delta > 50) { e.stopPropagation(); prevPhoto(); }
            else if (delta < -50) { e.stopPropagation(); nextPhoto(); }
          }}
        >
          <LightboxClose onClick={() => setLightboxIdx(null)}>✕</LightboxClose>
          <LightboxCounter>{lightboxIdx + 1} / {photos.length}</LightboxCounter>
          <LightboxNav side="left" onClick={e => { e.stopPropagation(); prevPhoto(); }}>‹</LightboxNav>
          <LightboxImg src={photos[lightboxIdx].thumbnailUrl || photos[lightboxIdx].fileUrl} alt={t('guest.lightbox_alt')} onClick={e => e.stopPropagation()} />
          <LightboxNav side="right" onClick={e => { e.stopPropagation(); nextPhoto(); }}>›</LightboxNav>
          <LightboxDownload
            href={photos[lightboxIdx].fileUrl}
            download={photos[lightboxIdx].originalFilename || 'foto.jpg'}
            onClick={e => e.stopPropagation()}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {t('guest.download')}
          </LightboxDownload>
        </Lightbox>
      )}
    </Wrapper>
  );
};

export default GuestEventPage;
